import React, { FC } from 'react';
import { ColorFunction } from '@styles/colors/types';
import { isPhone, isWeb } from '@helpers';
import { OP_INFO_TABS_WIDTH } from '@v2/pages/common/constants/base.constants';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import Svg from '@styles/icons/svg';
import { scale as initialScale } from '@components/frame';
import { GlassButton } from '@components/common/glassButton';
import TouchableElement from '@components/common/touchableElement';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import Picture, { PictureProps } from '@v2/components/imageHandling/components/Picture';
import { ImageContextForAdding, ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { useManageImagesDocumentsGallery } from './hooks/useManageImagesDocumentsGallery';
import DocTypeButton from '@v2/components/imageHandling/components/Header/components/DocTypeButton';
import ImageHandlingHeader, { ImageHandlingHeaderProps } from '@v2/components/imageHandling/components/Header/Header';
import { HeaderIconKey } from '@v2/components/imageHandling/components/Header/components/HeaderIcons/types';
import { isImageContextHasScopeId } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { IMAGE_HANDLING_HEADER_HEIGHT } from '@components/imageHandling/common';

export interface ImagesDocumentsGalleryPropsWeb
  extends Pick<PictureProps, 'withoutAttachItems'>,
    Pick<ImageHandlingHeaderProps, 'cropImage' | 'rotateImage' | 'deleteImage'> {
  images?: ImageDocumentStoreItem[];
  C: ColorFunction;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  containerHeight: number;
  containerWidth: number;
  bigSliderBg?: string;
  smallSliderBg?: string;
  glassButtonsStyle?: {
    docTypeButton?: StyleProp<ViewStyle>;
    expandViewButton?: StyleProp<ViewStyle>;
  };
  shouldUseColorWrapperForBigSliderBg?: boolean;
  shouldUseColorWrapperForSmallSliderBg?: boolean;
  withoutDocTypeButton?: boolean;
  singleImageInBigSlider?: boolean;
  withoutImageHandlingHeader?: boolean;
  imageHandlingHeaderBg?: string;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const ImagesDocumentsGallery: FC<ImagesDocumentsGalleryPropsWeb> = ({
  C,
  selectedIndex,
  setSelectedIndex,
  containerHeight,
  containerWidth,
  images: imagesFromProps,
  singleImageInBigSlider,
  imageHandlingHeaderBg,
  withoutImageHandlingHeader,
  withoutAttachItems,
  glassButtonsStyle,
  cropImage,
  rotateImage,
  deleteImage,
  withoutDocTypeButton = false,
  shouldUseColorWrapperForBigSliderBg = true,
  shouldUseColorWrapperForSmallSliderBg = true,
  bigSliderBg = isWeb ? 'Background/Light/System' : 'Background/Light/Panel',
  smallSliderBg = 'Background/Light/Panel',
  scale = initialScale,
  font = initialFont,
}) => {
  const {
    expandViewPosition,
    imagesList,
    isSingleImage,
    onChangeSelectedIndex,
    onExpandViewPress,
    onImagePress,
    onSuccessDocTypeChange,
    selectedImage,
    shouldDisplayExpandImageButton,
    shouldDisplayDocTypeButton,
    validSelectedIndex,
    webExpandControlConfig,
    imagesDocumentsLoading,
    imageContext,
  } = useManageImagesDocumentsGallery({ selectedIndex, setSelectedIndex, images: imagesFromProps, withoutDocTypeButton, withoutImageHandlingHeader });

  
  const imageProps: PictureProps = {
    images: imagesList,
    imageContext,
    showActions: true,
    withoutBackButtonForNoData: true,
    C,
    triggerShowActions: () => {},
    selectedIndex: validSelectedIndex,
    setSelectedIndex: onChangeSelectedIndex,
    isSingleImage,
    height: containerHeight - (!withoutImageHandlingHeader ? scale(IMAGE_HANDLING_HEADER_HEIGHT) : 0),
    width: containerWidth,
    onImagePress: !isWeb ? onImagePress : () => {},
    smallSliderBg,
    bg: bigSliderBg,
    shouldUseColorWrapperForBigSliderBg,
    shouldUseColorWrapperForSmallSliderBg,
    loaded: !imagesDocumentsLoading && validSelectedIndex >= 0,
    withoutAttachItems,
    singleImageInBigSlider,
    scale,
    font,
  };

  const imageContextHasScopeId = isImageContextHasScopeId(imageContext);
  const imageContextForAdding: ImageContextForAdding = imageContextHasScopeId ? { tags: [imageContext] } : {};

  const webTabletContainerStyles: StyleProp<ViewStyle> = [
    {
      position: 'relative',
      height: containerHeight,
    },
    !withoutImageHandlingHeader && {
      borderTopColor: C('Hairline/Light/Transpar'),
      borderTopWidth: StyleSheet.hairlineWidth,
    },
  ];

  const HeaderComponent = (
    <ImageHandlingHeader
      C={C}
      cropImage={cropImage}
      rotateImage={rotateImage}
      deleteImage={deleteImage}
      imageContextForAdding={imageContextForAdding}
      images={imagesList}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
      showActions={true}
      bg={bigSliderBg}
      borderColor={C('Hairline/Light/Transpar')}
      withNotchTop={false}
      enabledIcons={[HeaderIconKey.CROP, HeaderIconKey.ROTATE, HeaderIconKey.SHARE, HeaderIconKey.MORE, HeaderIconKey.EXPAND]}
      onExpandIconPress={onExpandViewPress}
    />
  );

  const DocTypeButtonOnImage = (
    <TouchableElement>
      {({ hovered, pressHoverColor, boxStyle }) => (
        <View
          style={[
            {
              top: scale(9),
              left: scale(0),
              position: 'absolute',
              zIndex: 10000000000,
            },
            glassButtonsStyle?.docTypeButton,
          ]}
        >
          <GlassButton
            style={[
              boxStyle,
              {
                borderRadius: scale(!isPhone ? 18 : 10),
                paddingHorizontal: scale(0),
                paddingVertical: scale(0),
                justifyContent: 'center',
                alignItems: 'center',
              },
            ]}
            scale={scale}
          >
            {({ hovered }) => (
              <DocTypeButton
                {...{
                  images: imagesList,
                  C,
                  scale,
                  font,
                  img: selectedImage,
                  onSuccessDocTypeChange,
                  containerStyles: {
                    backgroundColor: 'transparent',
                    borderColor: 'transparent',
                  },
                  textStyles: {
                    color: hovered ? C('Label/Light/Primary') : C('Label/Light/AlwaysDark Primary'),
                  },
                  leftIconStyles: { color: hovered ? C('Label/Light/Primary') : C('Label/Light/AlwaysDark Primary') },
                  rightIconStyles: { color: hovered ? 'Label/Light/Primary' : 'Label/Light/AlwaysDark Primary' },
                  shouldModalPositionBeRelatedToButton: !isWeb && !isPhone,
                  configStyles: {
                    marginLeft: scale(OP_INFO_TABS_WIDTH),
                    ...(!isWeb && !isPhone ? { borderRadius: 33 } : {}),
                  },
                }}
              />
            )}
          </GlassButton>
        </View>
      )}
    </TouchableElement>
  );

  if (isWeb) {
    return (
      <View style={webTabletContainerStyles}>
        {shouldDisplayDocTypeButton && DocTypeButtonOnImage}

        {shouldDisplayExpandImageButton && (
          <TouchableElement onPress={onExpandViewPress}>
            {({ hovered, pressHoverColor, boxStyle }) => (
              <View
                style={[
                  boxStyle,
                  {
                    top: scale(-expandViewPosition + 9),
                    right: scale(-expandViewPosition),
                    position: 'absolute',
                    zIndex: 10000000000,
                    width: scale(webExpandControlConfig.containerSize),
                    height: scale(webExpandControlConfig.containerSize),
                    borderRadius: scale(webExpandControlConfig.containerSize),
                  },
                  glassButtonsStyle?.expandViewButton,
                ]}
              >
                <GlassButton
                  style={[
                    {
                      borderRadius: scale(webExpandControlConfig.size),
                      height: scale(webExpandControlConfig.size),
                      width: scale(webExpandControlConfig.size),
                      padding: scale(0),
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'absolute',
                      left: scale(expandViewPosition),
                      top: scale(expandViewPosition),
                    },
                  ]}
                  scale={scale}
                >
                  {({ hovered }) => (
                    <Svg
                      icon="expandView"
                      width={scale(16)}
                      height={scale(16)}
                      color={C(hovered ? 'Label/Light/Primary' : 'Label/Light/AlwaysDark Primary')}
                    />
                  )}
                </GlassButton>
              </View>
            )}
          </TouchableElement>
        )}

        {!withoutImageHandlingHeader && HeaderComponent}
        <View style={[{ flex: 1 }, !withoutImageHandlingHeader && { marginTop: scale(IMAGE_HANDLING_HEADER_HEIGHT) }]}>
          <Picture {...imageProps} />
        </View>
      </View>
    );
  }

  if (!isPhone) {
    return (
      <View style={webTabletContainerStyles}>
        {shouldDisplayDocTypeButton && DocTypeButtonOnImage}
        {!withoutImageHandlingHeader && HeaderComponent}
        <View style={[{ flex: 1 }, !withoutImageHandlingHeader && { marginTop: scale(IMAGE_HANDLING_HEADER_HEIGHT) }]}>
          <Picture {...imageProps} />
        </View>
      </View>
    );
  }

  return <Picture {...imageProps} />;
};

export default ImagesDocumentsGallery;
